import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Backdrop,
  LinearProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Assignment,
  AutoAwesome,
  Person,
  CheckCircle,
} from '@mui/icons-material';
import { workflowService, WorkflowCreateRequest, ChatWorkflowCreateRequest, ChatMessage } from '../services/workflowService';
import { DetectedForm } from '../services/api';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  regulatoryBodies?: string[];
  generationId?: string;
  questionComplexity?: 'simple' | 'complex' | 'process';
  forms?: DetectedForm[];
}

interface ConvertToWorkflowDialogProps {
  open: boolean;
  onClose: () => void;
  messages: Message[];
  onWorkflowCreated: (workflow: any) => void;
}

const ConvertToWorkflowDialog: React.FC<ConvertToWorkflowDialogProps> = ({
  open,
  onClose,
  messages,
  onWorkflowCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [loadingDeviceClasses, setLoadingDeviceClasses] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [useOutputBasedConversion, setUseOutputBasedConversion] = useState(true);
  const lastUserEditAt = useRef<number>(0);
  const messagesRef = useRef<Message[]>(messages);

  // Form data
  const [workflowData, setWorkflowData] = useState<WorkflowCreateRequest>({
    name: '',
    description: '',
    jurisdiction: '',
    device_class: '',
    device_type: '',
    device_category: '',
  });

  // Available options
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [deviceClasses, setDeviceClasses] = useState<Array<{value: string, display_name: string}>>([]);

  // AI-suggested values
  const [suggestedValues, setSuggestedValues] = useState<{
    jurisdiction?: string;
    device_class?: string;
    device_type?: string;
    device_category?: string;
    chatContext?: {
      specificRequirements: string[];
      mentionedForms: string[];
      mentionedDocuments: string[];
      keyTopics: string[];
      timeline: string | null;
      specialConsiderations: string[];
    };
  }>({});

  const loadOptions = async () => {
    try {
      setLoading(true);
      const jurisdictionsData = await workflowService.getJurisdictions();
      // Filter to only show the main regulations: US FDA, EU MDR
      const allowedJurisdictions = ['us_fda', 'eu_mdr'];
      const filteredJurisdictions = jurisdictionsData.filter((jurisdiction: string) => 
        allowedJurisdictions.includes(jurisdiction)
      );
      setJurisdictions(filteredJurisdictions);
    } catch (err) {
      setError('Failed to load workflow options');
    } finally {
      setLoading(false);
    }
  };

  const loadDeviceClasses = async (jurisdiction: string) => {
    try {
      setLoadingDeviceClasses(true);
      const response = await workflowService.getDeviceClassesByJurisdiction(jurisdiction);
      setDeviceClasses(response.device_classes);
    } catch (err) {
      console.error('Failed to load device classes:', err);
      setDeviceClasses([]);
    } finally {
      setLoadingDeviceClasses(false);
    }
  };

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const analyzeConversation = useCallback(async () => {
    const analysisStartedAt = Date.now();
    setAnalyzing(true);
    
    // Add a small delay to show loading state (analysis is fast but we want to show feedback)
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Analyze the conversation to suggest workflow values
    const conversationText = messagesRef.current
      .map(msg => msg.content)
      .join(' ')
      .toLowerCase();

    const suggestions: any = {};

    // Simple, clear jurisdiction detection - US and EU only
    if (
      conversationText.includes('fda') || 
      conversationText.includes('usa') || 
      conversationText.includes('united states') || 
      conversationText.includes('us fda') ||
      conversationText.includes('510k') ||
      conversationText.includes('510(k)') ||
      conversationText.includes('pma') ||
      conversationText.includes('premarket notification') ||
      conversationText.includes('premarket approval') ||
      conversationText.includes('cdrh') ||
      conversationText.includes('center for devices')
    ) {
      suggestions.jurisdiction = 'us_fda';
    }
    // EU MDR detection
    else if (
      conversationText.includes('eu mdr') ||
      conversationText.includes('european union') ||
      conversationText.includes('eu regulation') ||
      conversationText.includes('regulation 2017/745') ||
      conversationText.includes('regulation (eu) 2017/745') ||
      (conversationText.includes('eu') && conversationText.includes('mdr')) ||
      (conversationText.includes('europe') && conversationText.includes('mdr')) ||
      (conversationText.includes('eu') && conversationText.includes('ce marking')) ||
      (conversationText.includes('eu') && conversationText.includes('notified body')) ||
      (conversationText.includes('europe') && conversationText.includes('ce marking')) ||
      conversationText.includes('eudamed')
    ) {
      suggestions.jurisdiction = 'eu_mdr';
    }
    // If no regulation is explicitly mentioned or scores are too low, leave suggestions.jurisdiction undefined
    // This will prompt the user to select a regulation

    // Enhanced device class analysis - will be updated based on selected jurisdiction
    if (conversationText.includes('class i') || conversationText.includes('class_i') || conversationText.includes('low risk')) {
      suggestions.device_class = 'class_i'; // Will be mapped to jurisdiction-specific class
    } else if (conversationText.includes('class iia') || conversationText.includes('class_iia') || conversationText.includes('medium risk')) {
      suggestions.device_class = 'class_iia'; // Will be mapped to jurisdiction-specific class
    } else if (conversationText.includes('class iib') || conversationText.includes('class_iib') || conversationText.includes('higher risk')) {
      suggestions.device_class = 'class_iib'; // Will be mapped to jurisdiction-specific class
    } else if (conversationText.includes('class iii') || conversationText.includes('class_iii') || conversationText.includes('high risk')) {
      suggestions.device_class = 'class_iii'; // Will be mapped to jurisdiction-specific class
    }

    // Enhanced device type analysis
    if (conversationText.includes('heart rate') || conversationText.includes('cardiac') || conversationText.includes('ecg') || conversationText.includes('pulse')) {
      suggestions.device_type = 'Heart Rate Monitor';
    } else if (conversationText.includes('surgical') || conversationText.includes('surgery') || conversationText.includes('scalpel') || conversationText.includes('suture')) {
      suggestions.device_type = 'Surgical Instrument';
    } else if (conversationText.includes('diagnostic') || conversationText.includes('diagnosis') || conversationText.includes('test') || conversationText.includes('analyzer')) {
      suggestions.device_type = 'Diagnostic Device';
    } else if (conversationText.includes('implant') || conversationText.includes('implantable') || conversationText.includes('pacemaker') || conversationText.includes('stent')) {
      suggestions.device_type = 'Implantable Device';
    } else if (conversationText.includes('wearable') || conversationText.includes('fitness') || conversationText.includes('smartwatch') || conversationText.includes('band')) {
      suggestions.device_type = 'Wearable Device';
    } else if (conversationText.includes('inhaler') || conversationText.includes('respiratory') || conversationText.includes('breathing')) {
      suggestions.device_type = 'Respiratory Device';
    } else if (conversationText.includes('glucose') || conversationText.includes('diabetes') || conversationText.includes('blood sugar')) {
      suggestions.device_type = 'Glucose Monitor';
    }

    // Enhanced device category analysis
    if (conversationText.includes('wearable') || conversationText.includes('fitness') || conversationText.includes('smartwatch')) {
      suggestions.device_category = 'Wearable';
    } else if (conversationText.includes('implant') || conversationText.includes('implantable') || conversationText.includes('internal')) {
      suggestions.device_category = 'Implantable';
    } else if (conversationText.includes('diagnostic') || conversationText.includes('diagnosis') || conversationText.includes('testing')) {
      suggestions.device_category = 'Diagnostic';
    } else if (conversationText.includes('surgical') || conversationText.includes('surgery') || conversationText.includes('invasive')) {
      suggestions.device_category = 'Surgical';
    } else if (conversationText.includes('therapeutic') || conversationText.includes('treatment') || conversationText.includes('therapy')) {
      suggestions.device_category = 'Therapeutic';
    } else if (conversationText.includes('monitoring') || conversationText.includes('monitor') || conversationText.includes('tracking')) {
      suggestions.device_category = 'Monitoring';
    }

    // Extract chat-specific requirements and context
    const chatContext = extractChatContext(conversationText);
    suggestions.chatContext = chatContext;

    setSuggestedValues(suggestions);

    // Auto-fill form with suggestions unless the user edited during analysis
    if (lastUserEditAt.current <= analysisStartedAt) {
      setWorkflowData(prev => {
        const next = { ...prev };
        if (!prev.name) {
          next.name = `Workflow from Chat - ${new Date().toLocaleDateString()}`;
        }
        if (!prev.jurisdiction && suggestions.jurisdiction) {
          next.jurisdiction = suggestions.jurisdiction;
        }
        if (!prev.device_type && suggestions.device_type) {
          next.device_type = suggestions.device_type;
        }
        if (!prev.device_category && suggestions.device_category) {
          next.device_category = suggestions.device_category;
        }
        // Don't auto-fill device_class - let it be loaded based on jurisdiction
        next.device_class = prev.device_class;
        return next;
      });
    }
    
    // If jurisdiction is suggested, load device classes for it
    if (suggestions.jurisdiction) {
      await loadDeviceClasses(suggestions.jurisdiction);
    }
    
    setAnalyzing(false);
  }, []);

  const extractChatContext = (conversationText: string) => {
    const context = {
      specificRequirements: [] as string[],
      mentionedForms: [] as string[],
      mentionedDocuments: [] as string[],
      keyTopics: [] as string[],
      timeline: null as string | null,
      specialConsiderations: [] as string[]
    };

    // Extract specific requirements mentioned
    const requirementPatterns = [
      /clinical trial/i,
      /risk management/i,
      /quality management/i,
      /technical documentation/i,
      /clinical evaluation/i,
      /conformity assessment/i,
      /ce marking/i,
      /iso 13485/i,
      /mdr annex/i,
      /notified body/i,
      /post-market surveillance/i,
      /vigilance/i,
      /adverse events/i
    ];

    requirementPatterns.forEach(pattern => {
      if (pattern.test(conversationText)) {
        context.specificRequirements.push(pattern.source.replace(/[/()]/g, '').replace('i', ''));
      }
    });

    // Extract mentioned forms
    const formPatterns = [
      /md-3/i,
      /md-5/i,
      /md-14/i,
      /md-16/i,
      /510k/i,
      /pma/i,
      /declaration of conformity/i,
      /technical file/i,
      /clinical evaluation report/i,
      /risk management file/i
    ];

    formPatterns.forEach(pattern => {
      if (pattern.test(conversationText)) {
        context.mentionedForms.push(pattern.source.replace(/[/()]/g, '').replace('i', ''));
      }
    });

    // Extract key topics
    const topicPatterns = [
      /manufacturing/i,
      /distribution/i,
      /import/i,
      /export/i,
      /post-market surveillance/i,
      /vigilance/i,
      /adverse events/i,
      /recall/i,
      /clinical data/i,
      /safety/i,
      /efficacy/i
    ];

    topicPatterns.forEach(pattern => {
      if (pattern.test(conversationText)) {
        context.keyTopics.push(pattern.source.replace(/[/()]/g, '').replace('i', ''));
      }
    });

    // Extract timeline mentions
    const timelineMatch = conversationText.match(/(\d+)\s*(days?|weeks?|months?|years?)/i);
    if (timelineMatch) {
      context.timeline = timelineMatch[0];
    }

    // Extract special considerations
    const considerationPatterns = [
      /pediatric/i,
      /elderly/i,
      /pregnancy/i,
      /sterile/i,
      /single-use/i,
      /reusable/i,
      /software/i,
      /ai/i,
      /machine learning/i,
      /cybersecurity/i
    ];

    considerationPatterns.forEach(pattern => {
      if (pattern.test(conversationText)) {
        context.specialConsiderations.push(pattern.source.replace(/[/()]/g, '').replace('i', ''));
      }
    });

    return context;
  };

  // Load available options and analyze conversation on mount
  useEffect(() => {
    if (open) {
      loadOptions();
      analyzeConversation();
    }
  }, [open, analyzeConversation]);

  // Load device classes when jurisdiction changes
  useEffect(() => {
    if (workflowData.jurisdiction) {
      // Clear device class when regulation changes
      setWorkflowData(prev => ({ ...prev, device_class: '' }));
      // Load device classes for the selected regulation
      loadDeviceClasses(workflowData.jurisdiction);
    } else {
      // Clear device classes if no regulation is selected
      setDeviceClasses([]);
    }
  }, [workflowData.jurisdiction]);

  const handleCreateWorkflow = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Convert messages to the format expected by the API
      // Include forms from assistant messages (these are the forms shown in chat)
      const chatMessages: ChatMessage[] = messages.map(msg => ({
        id: msg.id,
        type: msg.type,
        content: msg.content,
        timestamp: msg.timestamp.toISOString(),
        // Include forms for assistant messages (from chat response)
        forms: msg.type === 'assistant' && msg.forms ? msg.forms : undefined
      }));
      
      // Create chat-based workflow request
      const chatWorkflowRequest: ChatWorkflowCreateRequest = {
        name: workflowData.name,
        description: workflowData.description,
        jurisdiction: workflowData.jurisdiction,
        device_class: workflowData.device_class,
        device_type: workflowData.device_type,
        device_category: workflowData.device_category,
        chat_context: suggestedValues.chatContext || {},
        messages: chatMessages,
        use_output_based_conversion: useOutputBasedConversion
      };
      
      const workflow = await workflowService.createChatWorkflow(chatWorkflowRequest);
      setSuccess('Chat-based workflow created successfully!');
      
      // Immediately open the created workflow and close dialog
      onWorkflowCreated(workflow);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create chat-based workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setWorkflowData({
      name: '',
      description: '',
      jurisdiction: '',
      device_class: '',
      device_type: '',
      device_category: '',
    });
    setSuggestedValues({});
    setError(null);
    setSuccess(null);
    setUseOutputBasedConversion(true);
    onClose();
  };

  const applySuggestion = (field: keyof typeof suggestedValues, value: string) => {
    setWorkflowData(prev => {
      const updated = { ...prev, [field]: value };
      // If jurisdiction is being applied, clear device_class and load new device classes
      if (field === 'jurisdiction') {
        updated.device_class = '';
        loadDeviceClasses(value);
      }
      return updated;
    });
  };

  const renderConversationSummary = () => {
    const userMessages = messages.filter(msg => msg.type === 'user');
    const assistantMessages = messages.filter(msg => msg.type === 'assistant');
    
    return (
      <Card sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
            <AutoAwesome sx={{ mr: 1, verticalAlign: 'middle' }} />
            Conversation Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
            {userMessages.length} user questions • {assistantMessages.length} AI responses
          </Typography>
          <Divider sx={{ my: 1 }} />
          {userMessages.slice(0, 3).map((msg, index) => (
            <Box key={msg.id} sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                <Person sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Q{index + 1}:
              </Typography>
              <Typography
                variant="body2"
                title={msg.content}
                noWrap
                sx={{
                  ml: 2,
                  fontStyle: 'italic',
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {msg.content}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderSuggestions = () => {
    if (Object.keys(suggestedValues).length === 0) return null;

    return (
      <Card sx={{ mb: 2, bgcolor: 'primary.50' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
            <CheckCircle sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            AI Suggestions
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
            Based on your conversation, we suggest these values:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {suggestedValues.jurisdiction && (
              <Chip
                label={`Regulation: ${suggestedValues.jurisdiction.replace('_', ' ').toUpperCase()}`}
                color="primary"
                size="small"
                onClick={() => applySuggestion('jurisdiction', suggestedValues.jurisdiction!)}
              />
            )}
            {suggestedValues.device_class && (
              <Chip
                label={`Class: ${suggestedValues.device_class.replace('_', ' ').toUpperCase()}`}
                color="primary"
                size="small"
                onClick={() => applySuggestion('device_class', suggestedValues.device_class!)}
              />
            )}
            {suggestedValues.device_type && (
              <Chip
                label={`Type: ${suggestedValues.device_type}`}
                color="primary"
                size="small"
                onClick={() => applySuggestion('device_type', suggestedValues.device_type!)}
              />
            )}
            {suggestedValues.device_category && (
              <Chip
                label={`Category: ${suggestedValues.device_category}`}
                color="primary"
                size="small"
                onClick={() => applySuggestion('device_category', suggestedValues.device_category!)}
              />
            )}
          </Box>
          
          {/* Display extracted chat context */}
          {suggestedValues.chatContext && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1 }}>
                Extracted from your conversation:
              </Typography>
              
              {suggestedValues.chatContext.specificRequirements.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>Requirements:</strong> {suggestedValues.chatContext.specificRequirements.join(', ')}
                  </Typography>
                </Box>
              )}
              
              {suggestedValues.chatContext.mentionedForms.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>Forms mentioned:</strong> {suggestedValues.chatContext.mentionedForms.join(', ')}
                  </Typography>
                </Box>
              )}
              
              {suggestedValues.chatContext.keyTopics.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>Key topics:</strong> {suggestedValues.chatContext.keyTopics.join(', ')}
                  </Typography>
                </Box>
              )}
              
              {suggestedValues.chatContext.timeline && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>Timeline mentioned:</strong> {suggestedValues.chatContext.timeline}
                  </Typography>
                </Box>
              )}
              
              {suggestedValues.chatContext.specialConsiderations.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>Special considerations:</strong> {suggestedValues.chatContext.specialConsiderations.join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : handleClose} maxWidth="md" fullWidth>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal - 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Creating Custom Workflow...</Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we generate your personalized compliance workflow
          </Typography>
        </Box>
      </Backdrop>
      
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment />
          <Typography variant="h6" component="span">
            Convert Chat to Custom Workflow
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {(analyzing || loading) && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
              {analyzing ? 'Analyzing conversation...' : 'Loading workflow options...'}
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            <strong>Custom Workflow Generation:</strong> This will create a personalized compliance workflow 
            based on your chat conversation. The AI will analyze your discussion to generate custom steps, 
            requirements, and considerations specific to your device and regulatory needs.
          </Typography>
        </Alert>

        {renderConversationSummary()}
        {renderSuggestions()}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Conversion Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={useOutputBasedConversion}
                  onChange={(event) => setUseOutputBasedConversion(event.target.checked)}
                  color="primary"
                />
              }
              label="Use output-based conversion (recommended)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Parses the latest output-style answer into steps, timeline deadlines, and requirements.
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1rem', fontWeight: 600 }}>
          Workflow Details
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Workflow Name"
            value={workflowData.name}
            onChange={(e) => {
              lastUserEditAt.current = Date.now();
              setWorkflowData(prev => ({ ...prev, name: e.target.value }));
            }}
            placeholder="e.g., Heart Rate Monitor - EU MDR Compliance"
            helperText="Give your compliance workflow a descriptive name"
            FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
            InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
            inputProps={{ sx: { fontSize: '0.875rem' } }}
            disabled={loading || analyzing}
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl fullWidth sx={{ minWidth: 250 }}>
              <InputLabel id="jurisdiction-label" sx={{ fontSize: '0.875rem' }}>Regulation</InputLabel>
              <Select
                labelId="jurisdiction-label"
                value={workflowData.jurisdiction}
                onChange={(e) => {
                  lastUserEditAt.current = Date.now();
                  setWorkflowData(prev => ({ ...prev, jurisdiction: e.target.value }));
                }}
                label="Regulation"
                sx={{ fontSize: '0.875rem' }}
                disabled={loading || analyzing}
              >
                {jurisdictions.map((jurisdiction) => (
                  <MenuItem key={jurisdiction} value={jurisdiction} sx={{ fontSize: '0.875rem' }}>
                    {jurisdiction?.replace('_', ' ').toUpperCase() || jurisdiction}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ minWidth: 250 }}>
              <InputLabel id="device-class-label" sx={{ fontSize: '0.875rem' }}>Device Class</InputLabel>
              <Select
                labelId="device-class-label"
                value={workflowData.device_class}
                onChange={(e) => {
                  lastUserEditAt.current = Date.now();
                  setWorkflowData(prev => ({ ...prev, device_class: e.target.value }));
                }}
                label="Device Class"
                sx={{ fontSize: '0.875rem' }}
                disabled={loading || analyzing || !workflowData.jurisdiction || loadingDeviceClasses}
              >
                {loadingDeviceClasses ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      Loading device classes...
                    </Box>
                  </MenuItem>
                ) : deviceClasses.length === 0 && workflowData.jurisdiction ? (
                  <MenuItem disabled>No device classes available</MenuItem>
                ) : deviceClasses.length === 0 ? (
                  <MenuItem disabled>Select a regulation first</MenuItem>
                ) : (
                  deviceClasses.map((deviceClass) => (
                    <MenuItem key={deviceClass.value} value={deviceClass.value} sx={{ fontSize: '0.875rem' }}>
                      {deviceClass.display_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              label="Device Type"
              value={workflowData.device_type}
            onChange={(e) => {
              lastUserEditAt.current = Date.now();
              setWorkflowData(prev => ({ ...prev, device_type: e.target.value }));
            }}
              placeholder="e.g., Heart Rate Monitor, Surgical Instrument"
              sx={{ minWidth: 250 }}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ sx: { fontSize: '0.875rem' } }}
              disabled={loading || analyzing}
            />
            
            <TextField
              fullWidth
              label="Device Category"
              value={workflowData.device_category}
            onChange={(e) => {
              lastUserEditAt.current = Date.now();
              setWorkflowData(prev => ({ ...prev, device_category: e.target.value }));
            }}
              placeholder="e.g., Wearable, Implantable, Diagnostic"
              sx={{ minWidth: 250 }}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ sx: { fontSize: '0.875rem' } }}
              disabled={loading || analyzing}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateWorkflow}
          disabled={loading || analyzing || !workflowData.name || !workflowData.jurisdiction || !workflowData.device_class}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Creating Custom Workflow...' : 'Create Custom Workflow'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertToWorkflowDialog;
